import
{
    IChatRepositoryMessage,
    IChatRepositoryMessageBot, IChatRepositoryMessageUser
} from "@core/office/office-chat-repository/office-chat-repository.types";
import { R2AttachmentType } from "@common/types/api/r2-types";
import type { IR2Attachment } from "@common/types/api/r2-types";
import { chatConstants } from '@core/constants/chat.constants';
import type { IChatMessage, IChatMessageBot, IChatMessageUser } from '@core/types/chat-message.types';

interface ILegacyChatMessageBotEmbed
{
    image: {
        content: string;
        url: string;
        mimeType: string;
    };
}

export class ChatUtilsService
{
    constructor()
    {
    }

    public isSenderBot(message: IChatMessage): message is IChatMessageBot
    {
        return message?.sender === chatConstants.MESSAGE_SENDER.bot;
    }

    public isSenderUser(message: IChatMessage): message is IChatMessageUser
    {
        return message.sender === chatConstants.MESSAGE_SENDER.user;
    }

    public isSenderRepositoryBot(message: IChatRepositoryMessage): message is IChatRepositoryMessageBot
    {
        return message?.sender === chatConstants.MESSAGE_SENDER.bot;
    }

    public isSenderRepositoryUser(message: IChatRepositoryMessage): message is IChatRepositoryMessageUser
    {
        return message.sender === chatConstants.MESSAGE_SENDER.user;
    }

    public isValueImg(message: IChatMessageBot): boolean
    {
        return this.getMessageImageSources(message).length > 0 || this.getMessageImageFileIds(message).length > 0;
    }

    public getMessageValue(message: IChatMessageBot): string
    {
        if (message.value)
        {
            return message.value;
        }

        const [ firstLegacyEmbed ] = this.getLegacyEmbeds(message);
        return firstLegacyEmbed?.image?.content || '';
    }

    /**
     * Returns the message text with embedded `<img>` tags (and bare data-image payloads)
     * removed, so it can be safely fed to the markdown renderer. marked's lheading regex
     * overflows the call stack on single "lines" larger than a few MB, which embedded
     * base64 images easily exceed.
     */
    public getMessageTextValue(message: IChatMessageBot): string
    {
        const messageValue = this.getMessageValue(message);

        if (this.extractDirectImageSource(messageValue).length > 0)
        {
            return '';
        }

        return messageValue.replace(/<img[^>]*>/gi, '').trim();
    }

    public getMessageImageSources(message: IChatMessageBot): string[]
    {
        const imageSources = new Set<string>();

        const messageValue = this.getMessageValue(message);
        this.extractImageSourcesFromHtml(messageValue).forEach(src => imageSources.add(src));
        this.extractDirectImageSource(messageValue).forEach(src => imageSources.add(src));

        this.getLegacyEmbeds(message).forEach(embed =>
        {
            if (embed?.image?.url)
            {
                imageSources.add(embed.image.url);
            }

            if (embed?.image?.content)
            {
                this.extractImageSourcesFromHtml(embed.image.content).forEach(src => imageSources.add(src));
                this.extractDirectImageSource(embed.image.content).forEach(src => imageSources.add(src));
            }
        });

        this.getImageAttachments(message).forEach(attachment =>
        {
            if (attachment.data)
            {
                this.extractImageSourcesFromHtml(attachment.data).forEach(src => imageSources.add(src));
                this.extractDirectImageSource(attachment.data).forEach(src => imageSources.add(src));
            }
        });

        return Array.from(imageSources);
    }

    public getMessageImageFileIds(message: IChatMessageBot): string[]
    {
        return this.getImageAttachments(message)
            .map(attachment => attachment.file_id || '')
            .filter(Boolean);
    }

    private getImageAttachments(message: IChatMessageBot): IR2Attachment[]
    {
        const options = message.options as (typeof message.options) & { attachments?: IR2Attachment[]; };

        if (!Array.isArray(options.attachments))
        {
            return [];
        }

        return options.attachments.filter(attachment =>
            attachment?.type === R2AttachmentType.Image
            || attachment?.mime_type?.toLowerCase().startsWith('image/')
        );
    }

    private getLegacyEmbeds(message: IChatMessageBot): ILegacyChatMessageBotEmbed[]
    {
        const options = message.options as (typeof message.options) & { embeds?: ILegacyChatMessageBotEmbed[]; };

        if (!Array.isArray(options.embeds))
        {
            return [];
        }

        return options.embeds;
    }

    private extractImageSourcesFromHtml(value: string): string[]
    {
        if (!value)
        {
            return [];
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(value, 'text/html');
        const images = Array.from(doc.querySelectorAll('img[src]'));

        return images
            .map(image => image.getAttribute('src') || '')
            .filter(Boolean);
    }

    private extractDirectImageSource(value: string): string[]
    {
        if (!value)
        {
            return [];
        }

        const source = value.trim();
        const isDataImage = source.startsWith('data:image/');
        const isHttpImage = /^(https?:\/\/).+\.(png|jpg|jpeg|gif|webp|bmp|svg)(\?.*)?$/i.test(source);

        if (isDataImage || isHttpImage)
        {
            return [ source ];
        }

        return [];
    }

}
